import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from './context/LanguageContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import CookieConsent from './components/common/CookieConsent';

import Home from './pages/Home';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ServiceDetail from './pages/ServiceDetail';
import SubServiceDetail from './pages/SubServiceDetail';
import About from './pages/About';
import Brands from './pages/Brands';
import Contact from './pages/Contact';
import Gizlilik from './pages/Gizlilik';
import KVKK from './pages/KVKK';
import Cerez from './pages/Cerez';
import DrLightCaseStudy from './pages/DrLightCaseStudy';
import Insights from './pages/Insights';
import InsightDetail from './pages/InsightDetail';
import NotFound from './pages/NotFound';

import { trackPageView } from './utils/analytics';

export const RouteWatcher: React.FC = () => {
  const { pathname } = useLocation();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      trackPageView(pathname);
    }

    // Sync language based on URL prefix
    if (pathname === '/en' || pathname.startsWith('/en/')) {
      if (language !== 'EN') setLanguage('EN');
    } else {
      if (language !== 'TR') setLanguage('TR');
    }
  }, [pathname, language, setLanguage]);

  return null;
};

export const AppLayout: React.FC = () => {
  const { language } = useLanguage();

  return (
    <>
      <RouteWatcher />

      {/* Skip to Main Content Link for Keyboard / Screen Reader Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-5 focus:py-2.5 focus:bg-white focus:text-zinc-950 focus:font-bold focus:text-xs focus:uppercase focus:rounded-full focus:shadow-2xl focus:ring-2 focus:ring-[#ff7a63]"
      >
        {language === 'TR' ? "Ana içeriğe geç" : "Skip to main content"}
      </a>

      <Helmet>
        <title>{language === 'TR' ? "WE24 Agency - Sinematik Prodüksiyon & Dijital İletişim Ajansı" : "WE24 Agency - Creative Production & Digital Agency"}</title>
        <meta
          name="description"
          content={
            language === 'TR'
              ? "Strateji, tasarım, prodüksiyon ve teknolojiyi tek çatı altında buluşturan yaratıcı iletişim ajansı."
              : "Global creative production and digital agency delivering cinema commercials, live broadcasting, VFX, and bespoke software."
          }
        />
      </Helmet>

      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none">
          <Routes>
            {/* Turkish Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/projeler" element={<Projects />} />
            <Route path="/projeler/dr-light-vaka-analizi" element={<DrLightCaseStudy />} />
            <Route path="/dr-light" element={<DrLightCaseStudy />} />
            <Route path="/projeler/:slug" element={<ProjectDetail />} />
            <Route path="/hizmetler" element={<ServiceDetail />} />
            <Route path="/hizmetler/:slug" element={<ServiceDetail />} />
            <Route path="/hizmetler/:categorySlug/:subserviceSlug" element={<SubServiceDetail />} />
            <Route path="/icgoruler" element={<Insights />} />
            <Route path="/icgoruler/:slug" element={<InsightDetail />} />
            <Route path="/blog" element={<Insights />} />
            <Route path="/hakkimizda" element={<About />} />
            <Route path="/markalar" element={<Brands />} />
            <Route path="/iletisim" element={<Contact />} />
            <Route path="/gizlilik" element={<Gizlilik />} />
            <Route path="/kvkk" element={<KVKK />} />
            <Route path="/cerez-politikasi" element={<Cerez />} />

            {/* English Routes */}
            <Route path="/en" element={<Home />} />
            <Route path="/en/projects" element={<Projects />} />
            <Route path="/en/projects/dr-light-case-study" element={<DrLightCaseStudy />} />
            <Route path="/en/dr-light" element={<DrLightCaseStudy />} />
            <Route path="/en/projects/:slug" element={<ProjectDetail />} />
            <Route path="/en/services" element={<ServiceDetail />} />
            <Route path="/en/services/:slug" element={<ServiceDetail />} />
            <Route path="/en/services/:categorySlug/:subserviceSlug" element={<SubServiceDetail />} />
            <Route path="/en/insights" element={<Insights />} />
            <Route path="/en/insights/:slug" element={<InsightDetail />} />
            <Route path="/en/blog" element={<Insights />} />
            <Route path="/en/about" element={<About />} />
            <Route path="/en/brands" element={<Brands />} />
            <Route path="/en/contact" element={<Contact />} />
            <Route path="/en/privacy" element={<Gizlilik />} />
            <Route path="/en/kvkk" element={<KVKK />} />
            <Route path="/en/cookie-policy" element={<Cerez />} />

            {/* 404 Catch-All */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <CookieConsent />
      </div>
    </>
  );
};
